import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  Typography
} from "@material-ui/core";
import { connect } from "react-redux";
import ClearOutlinedIcon from "@material-ui/icons/ClearOutlined";

import { closeAddDriverDialog } from "../../store/actions/common";
import Form from "../common/Form";
import Button from "@material-ui/core/Button";
import UnsavedChangesDialog from "../dialogs/UnsavedChangesDialog";
import { fade } from "@material-ui/core/styles/colorManipulator";
import { getFormattedPhoneAndCountry } from "../../utils/helpers";
import { emailRegex } from "../../utils/regExps";
import { PhoneNumberUtil } from "google-libphonenumber";
import API from "../../utils/axios";
import mixpanelWrapper from "../../utils/mixpanel";
import { setDrivers } from "../../store/actions/project";
import { useSnackbar } from "notistack";

const phoneUtil = PhoneNumberUtil.getInstance();
const initialState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  phoneCountry: ""
};
const initialErrors = {
  firstName: "",
  lastName: "",
  email: "",
  phone: ""
};

const useStyles = makeStyles(theme => ({
  dialogRoot: {
    zIndex: [1304, "!important"]
  },
  title: {
    paddingRight: theme.spacing(2),
    fontSize: theme.typography.pxToRem(20),
    fontWeight: theme.typography.fontWeightMedium
  },
  submitButton: {
    textTransform: "none",
    backgroundColor: theme.palette.primary.main,
    "&.Mui-disabled": {
      backgroundColor: theme.palette.primary.main,
      opacity: 0.38,
      color: "#fff"
    },
    "&:hover": {
      backgroundColor: [fade(theme.palette.primary.main, 0.7), "!important"]
    }
  },
  cancelButton: {
    backgroundColor: "transparent !important"
  }
}));

const AddDriverDialog = props => {
  const { open, closeAddDriverDialog, countryCode, sortedTags, setDrivers } =
    props;

  // General
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // States
  const [inputValues, setInputValues] = useState(initialState);
  const [selectedTags, setSelectedTags] = useState([]);
  const [errors, setErrors] = useState({ ...initialErrors });
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [changesDialog, setChangesDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  // Refs
  const autoFocusInput = useRef();

  // Callbacks
  const handleTagSelect = tag => {
    if (selectedTags.includes(tag.id)) {
      setSelectedTags(
        selectedTags.filter(selectedTag => selectedTag !== tag.id)
      );
    } else {
      setSelectedTags([...selectedTags, tag.id]);
    }
  };

  const checkForErrors = () => {
    const phone = inputValues.phone;
    const { numberVal } = getFormattedPhoneAndCountry(phone || "");

    const number =
      numberVal.length > 4 &&
      (inputValues.phone.includes("+")
        ? phoneUtil.parseAndKeepRawInput(inputValues.phone)
        : phoneUtil.parseAndKeepRawInput("+" + inputValues.phone));

    const newErrors = {
      email:
        inputValues.email.trim().length === 0
          ? "Please fill in this field"
          : !emailRegex.test(inputValues.email.trim())
          ? "Invalid email address"
          : "",
      firstName:
        inputValues.firstName.trim().length === 0
          ? "Please fill in this field"
          : "",
      lastName:
        inputValues.lastName.trim().length === 0
          ? "Please fill in this field"
          : "",
      phone:
        numberVal.length && inputValues.phoneCountry !== numberVal
          ? !number || !phoneUtil.isValidNumber(number)
            ? "Please enter a valid phone number"
            : ""
          : ""
    };

    setErrors(newErrors);

    return Object.values(newErrors).some(val => val);
  };

  const handleChange = event => {
    if (inputValues[event.target.name].trim() !== event.target.value.trim()) {
      setSaveDisabled(false);
    }
    setInputValues({ ...inputValues, [event.target.name]: event.target.value });
  };

  const handlePhoneChange = (value, country) => {
    setInputValues({
      ...inputValues,
      phone: value,
      phoneCountry: country && country.dialCode
    });
  };

  const close = () => {
    setInputValues(initialState);
    setErrors(initialErrors);
    setSaveDisabled(true);
    closeAddDriverDialog();
  };

  const warnClose = () => {
    if (Object.values(inputValues).some(Boolean)) {
      setChangesDialog(true);
    } else {
      close();
    }
  };

  const handleSave = () => {
    if (checkForErrors()) return;
    setLoading(true);

    const { numberVal } = getFormattedPhoneAndCountry(inputValues.phone || "");

    let data = [
      {
        first_name: inputValues.firstName,
        last_name: inputValues.lastName,
        email: inputValues.email,
        mobile_number:
          numberVal !== inputValues.phoneCountry && inputValues.phone.length > 1
            ? inputValues.phone
            : null,
        tags: selectedTags
      }
    ];

    API.post(`/v1/drivers/batch`, data)
      .then(res => {
        mixpanelWrapper.track("Driver Add");
        setDrivers(res.data);
        enqueueSnackbar(`Driver added.`);
        setLoading(false);
        close();
      })
      .catch(err => {
        if (err && err.status === 400 && err.data && err.data.errors[0]) {
          let field = err.data.errors[0].field;
          if (field === "driverMail") {
            field = "email";
          } else if (field === "firstname/lastname/email/phone") {
            enqueueSnackbar(err.data.errors[0].defaultMessage);
            setSaveDisabled(true);
          }
          setErrors({
            ...initialErrors,
            [field]: err.data.errors[0].defaultMessage
          });
        } else {
          enqueueSnackbar("Something went wrong, couldn't update driver");
        }
        setLoading(false);
        console.log(err);
      });
  };

  // Effects
  useEffect(() => {
    if (open && autoFocusInput.current) autoFocusInput.current.focus();
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        maxWidth="xs"
        classes={{
          root: classes.dialogRoot
        }}
      >
        <DialogTitle disableTypography>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <IconButton
                onClick={() => {
                  close();
                }}
                disabled={loading}
              >
                <ClearOutlinedIcon />
              </IconButton>
            </Grid>
            <Grid item xs>
              <Typography className={classes.title}>Add driver</Typography>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent dividers>
          <Form
            data={[
              [
                {
                  renderInputField: true,
                  label: "First name*",
                  name: "firstName",
                  type: "text",
                  inputRef: autoFocusInput,
                  autoFocus: true,
                  required: "*Required",
                  dataCy: "driver-firstName",
                  disabled: loading
                },
                {
                  renderInputField: true,
                  label: "Last name*",
                  name: "lastName",
                  type: "text",
                  dataCy: "driver-lastName",
                  required: "*Required",
                  disabled: loading
                }
              ],
              [
                {
                  renderInputField: true,
                  label: "Email*",
                  name: "email",
                  type: "text",
                  dataCy: "driver-email",
                  required: "*Required",
                  disabled: loading
                }
              ],
              [
                {
                  label: "Mobile phone",
                  name: "phone",
                  componentType: "phone",
                  dataCy: "driver-phone",
                  disabled: loading
                }
              ],
              [
                {
                  labelId: "tag-driver",
                  selectId: "tag-driver-select",
                  label: "Tags",
                  name: "tag",
                  type: "driver",
                  choose: false,
                  options: sortedTags,
                  selected: selectedTags,
                  onChange: handleTagSelect,
                  componentType: "tagSelect",
                  inDialog: true,
                  disabled: loading
                }
              ]
            ]}
            inputValues={inputValues}
            errorValues={errors}
            countryCode={countryCode}
            handleChange={handleChange}
            handlePhoneChange={handlePhoneChange}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={warnClose}
            type="button"
            variant="text"
            className={classes.cancelButton}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            color="primary"
            type="submit"
            variant="contained"
            disabled={saveDisabled || loading}
            className={classes.submitButton}
            onClick={handleSave}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <UnsavedChangesDialog
        open={changesDialog}
        handleClose={() => setChangesDialog(false)}
        action={close}
      />
    </>
  );
};

function mapStateToProps(state) {
  let countryCode;

  // Get country code from organisation site for non-freemium users
  const organisationSite = state.auth.userData.organisationSite;
  if (organisationSite && organisationSite.organisation) {
    countryCode = organisationSite.organisation.country;
  }

  return {
    open: state.common.addDriverDialogOpened,
    countryCode: countryCode || state.auth.userData.countryName,
    drivers: state.project.drivers || [],
    sortedTags: state.account.sortedTags
  };
}

export default connect(mapStateToProps, {
  closeAddDriverDialog,
  setDrivers
})(AddDriverDialog);
